/**
 * Fleet signals feed — NEW V3.1 page.
 *
 * Table over GET /api/v1/fleet/signals (the `signals` +
 * `signal_deliveries` journal) with market / agent / verb / status
 * filters, per-agent delivery status chips
 * (queued/sent/acked/rejected/expired/failed), latency and mismatch
 * warnings (§6 result double-checking). Live via SSE signal.* events.
 */
import { useMemo, useState } from "react";
import {
  Alert, Badge, Button, Code, Group, Paper, Select, Stack, Table, Text,
  Title, Tooltip,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  fleetApi,
  type DeliveryStatus,
  type SignalDelivery,
  type SignalVerb,
} from "../../api/fleet";
import { useMarket } from "../../hooks/useMarket";
import { useSSE } from "../../hooks/useSSE";

const VERBS: SignalVerb[] = ["place", "sell", "settle", "manage_pending", "prewarm"];
const STATUSES: DeliveryStatus[] = ["queued", "sent", "acked", "rejected", "expired", "failed"];

const STATUS_COLOR: Record<DeliveryStatus, string> = {
  queued: "gray",
  sent: "blue",
  acked: "green",
  rejected: "red",
  expired: "orange",
  failed: "red",
};

export function FleetSignalsPage() {
  const { markets } = useMarket();
  const [marketId, setMarketId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [verb, setVerb] = useState<SignalVerb | null>(null);
  const [status, setStatus] = useState<DeliveryStatus | null>(null);

  const agentsQ = useQuery({
    queryKey: ["fleet", "agents"],
    queryFn: () => fleetApi.agents.list(),
  });

  const q = useInfiniteQuery({
    queryKey: ["fleet", "signals", { marketId, agentId, verb, status }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fleetApi.fleet.signals({
        market_id: marketId ?? undefined,
        agent_id: agentId ?? undefined,
        verb: verb ?? undefined,
        status: status ?? undefined,
        cursor: pageParam,
        limit: 50,
      }),
    getNextPageParam: (last) =>
      last.has_more === false ? undefined : last.next_cursor ?? undefined,
    retry: false,
  });

  // signal.emitted / signal.result SSE events invalidate ["fleet","signals"].
  useSSE({});

  const agentName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agentsQ.data ?? []) m.set(a.id, a.display_name);
    return (id: string, fallback?: string) => fallback ?? m.get(id) ?? id.slice(0, 10);
  }, [agentsQ.data]);

  // The server returns `{ signals, next_cursor }`; older builds used `items`.
  const items = q.data?.pages.flatMap((p) => p.signals ?? p.items ?? []) ?? [];

  return (
    <>
      <Group justify="space-between" mb="lg" wrap="wrap">
        <Title order={2}>Signals</Title>
        <Group gap="xs" wrap="wrap">
          <Select
            placeholder="Market"
            data={markets.map((m) => ({ value: m.market_id, label: m.short_name }))}
            value={marketId}
            onChange={setMarketId}
            clearable
            w={150}
          />
          <Select
            placeholder="Agent"
            data={(agentsQ.data ?? []).map((a) => ({ value: a.id, label: a.display_name }))}
            value={agentId}
            onChange={setAgentId}
            clearable
            w={170}
          />
          <Select
            placeholder="Verb"
            data={VERBS}
            value={verb}
            onChange={(v) => setVerb(v as SignalVerb | null)}
            clearable
            w={150}
          />
          <Select
            placeholder="Delivery status"
            data={STATUSES}
            value={status}
            onChange={(v) => setStatus(v as DeliveryStatus | null)}
            clearable
            w={160}
          />
        </Group>
      </Group>

      {q.isError && (
        <Alert color="yellow" mb="md">
          Signal journal unavailable — the CC server does not expose
          <Code>/api/v1/fleet/signals</Code> yet.
        </Alert>
      )}
      {q.isLoading && <Text c="dimmed">Loading…</Text>}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <Text c="dimmed">No signals match the current filters.</Text>
      )}

      {items.length > 0 && (
        <Paper withBorder p={0}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Market / window</Table.Th>
                <Table.Th>Verb</Table.Th>
                <Table.Th>Config</Table.Th>
                <Table.Th>Deliveries</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((s) => (
                <Table.Tr key={s.signal_id}>
                  <Table.Td>
                    <Text size="xs" style={{ whiteSpace: "nowrap" }}>
                      {new Date(s.created_at).toLocaleString()}
                    </Text>
                    <Text size="xs" c="dimmed" ff="monospace" truncate maw={140}>
                      {s.signal_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="outline">{s.market_id}</Badge>
                    <Text size="xs" c="dimmed" truncate maw={180}>{s.slug}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={verbColor(s.verb)}>{s.verb}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" truncate maw={140}>
                      {s.config_version_id ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="wrap">
                      {(s.deliveries ?? []).map((d) => (
                        <DeliveryChip
                          key={d.agent_id}
                          d={d}
                          name={agentName(d.agent_id, d.agent_display_name)}
                        />
                      ))}
                      {s.deliveries.length === 0 && <Text size="xs" c="dimmed">—</Text>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {q.hasNextPage && (
        <Group justify="center" mt="md">
          <Button
            variant="light"
            onClick={() => void q.fetchNextPage()}
            loading={q.isFetchingNextPage}
          >
            Load more
          </Button>
        </Group>
      )}
    </>
  );
}

function DeliveryChip({ d, name }: { d: SignalDelivery; name: string }) {
  const hasMismatch = d.mismatch != null && Object.keys(d.mismatch).length > 0;
  const label = [
    name,
    d.status,
    d.latency_ms != null ? `${d.latency_ms}ms` : null,
  ].filter(Boolean).join(" · ");
  const tooltip = [
    d.error ? `error: ${d.error}` : null,
    hasMismatch ? `MISMATCH: ${JSON.stringify(d.mismatch)}` : null,
    d.acked_at ? `acked ${new Date(d.acked_at).toLocaleTimeString()}` : null,
  ].filter(Boolean).join(" — ") || label;

  return (
    <Tooltip label={tooltip} multiline maw={360}>
      <Badge
        size="sm"
        variant={hasMismatch ? "filled" : "light"}
        color={hasMismatch ? "red" : STATUS_COLOR[d.status] ?? "gray"}
        leftSection={hasMismatch ? <IconAlertTriangle size={12} /> : undefined}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

function verbColor(verb: SignalVerb): string {
  switch (verb) {
    case "place": return "blue";
    case "sell": return "grape";
    case "settle": return "teal";
    case "manage_pending": return "orange";
    case "prewarm": return "gray";
    default: return "gray";
  }
}
